import { t, ts } from "./i18n"

/**
 * Product photos from the phone: each one is shrunk ON THE PHONE into three widths — 480, 960 and
 * 1600 px, JPEG — and uploaded to HQ's own Worker (POST /hq-media/upload), which stores them in R2 for
 * media.highclub.store. A 12-megapixel iPhone photo never travels whole, and the storefront picks the
 * width a screen needs (the -w480 / -w960 / -w1600 naming is what its ProductImage reads).
 *
 * The product keeps the w1600 address; the other two are derived from it.
 */
export const WIDTHS = [480, 960, 1600] as const
const QUALITY = 0.82

/** A random photo id: 24 lowercase letters and digits (what the Worker accepts). */
export function photoId(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789"
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("")
}

/** The size to draw at: never wider than `max`, never enlarged, proportions kept. */
export function fitWidth(width: number, height: number, max: number): { width: number; height: number } {
  if (width <= max) return { width, height }
  return { width: max, height: Math.round((height * max) / width) }
}

/** Smaller widths of an uploaded photo, from its w1600 address. */
export const photoWidth = (url: string, width: (typeof WIDTHS)[number]) => url.replace(/-w1600\.(jpg|webp)$/, `-w${width}.$1`)

/** A photo file decoded and turned upright (its EXIF orientation applied), ready to draw. */
export async function decodePhoto(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" })
    } catch {
      // Older Safari: fall through to an <img>, which also honours the photo's orientation.
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.decoding = "async"
    image.src = url
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

function encode(source: ImageBitmap | HTMLImageElement, width: number, height: number): Promise<Blob> {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) throw new Error(t("This browser cannot resize photos."))
  context.imageSmoothingQuality = "high"
  context.drawImage(source, 0, 0, width, height)
  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error(t("Could not make the photo.")))), "image/jpeg", QUALITY))
}

/** Shrinks and uploads one photo; resolves to its w1600 address. `progress` gets 0–1. */
export async function uploadPhoto(file: File, progress?: (done: number) => void): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error(t("That file is not a photo."))
  const source = await decodePhoto(file)
  const natural = { width: "naturalWidth" in source ? source.naturalWidth : source.width, height: "naturalHeight" in source ? source.naturalHeight : source.height }
  const id = photoId()
  let main = ""
  for (const [index, width] of [...WIDTHS].reverse().entries()) {
    const size = fitWidth(natural.width, natural.height, width)
    const blob = await encode(source, size.width, size.height)
    const response = await fetch(`/hq-media/upload?id=${id}&w=${width}`, { method: "POST", body: blob, headers: { "content-type": "image/jpeg" }, credentials: "include" })
    const body = (await response.json().catch(() => ({}))) as { url?: string; error?: string }
    if (!response.ok || !body.url) throw new Error(body.error ? t("Photo upload: {error}", { error: ts(body.error) }) : t("Photo upload failed ({status}).", { status: response.status }))
    if (width === 1600) main = body.url
    progress?.((index + 1) / WIDTHS.length)
  }
  if ("close" in source) source.close()
  return main
}
